import { api } from "@lib/api";
import { queryKeys } from "@lib/api/queryKeys";
import { API_ENDPOINTS } from "@lib/config/constants";
import { useAuthStore } from "@/lib/auth/authStore";
import type { PermissionsSummary } from "@/lib/permissions/summary.types";
import { usePermissionStore } from "@/lib/stores/permissions.store";

export interface PermissionQueryResult {
  permissionsArray: unknown[];
  context: unknown;
  hasWildcard: boolean;
  summary: PermissionsSummary | null;
}

/**
 * Extract member array from Hydra collection or return as-is.
 */
function extractMember(data: unknown): unknown[] {
  const d = data as Record<string, unknown> | undefined;
  return (d?.member as unknown[]) || (data as unknown[]) || [];
}

/**
 * Normalize permission summary from API response (handles Hydra collection format).
 */
function normalizeSummary(
  summary: Record<string, unknown> | null | undefined,
): PermissionsSummary | null {
  if (!summary) return null;

  return {
    modules: extractMember(summary.modules) as readonly string[],
    module_actions: ((summary.module_actions as Record<string, unknown>)?.member ||
      summary.module_actions ||
      {}) as Record<string, Record<string, readonly string[]>>,
    filiales: extractMember(summary.filiales) as readonly string[],
    scopes: extractMember(summary.scopes) as readonly string[],
    user_type: (summary.user_type as string) || null,
  };
}

/**
 * Fetch permissions for a specific user by ID.
 */
export async function fetchPermissionsForUserById(userId: number | string) {
  try {
    const { data } = await api.get(API_ENDPOINTS.USER_PERMISSIONS(userId));

    const { permissions, context, hasWildcard: apiHasWildcard, summary } = data;
    const permissionsArray = (permissions?.member as unknown[]) || permissions || [];

    return {
      permissionsArray,
      context,
      hasWildcard: apiHasWildcard,
      summary: normalizeSummary(summary),
    };
  } catch (error) {
    console.error("Failed to fetch permissions for user:", userId, error);
    return {
      permissionsArray: [],
      context: null,
      hasWildcard: false,
      summary: null,
    };
  }
}

/**
 * Fetch raw permissions object for a user (used by refresh flow).
 */
export async function fetchUserPermissions(userId: number | string) {
  if (!userId) {
    throw new Error("User ID is required to fetch permissions.");
  }

  try {
    const response = await api.get(API_ENDPOINTS.USER_PERMISSIONS(userId));
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch permissions for user ${userId}:`, error);
    throw error;
  }
}

/**
 * Refresh permissions for the current user and update the store.
 */
export async function refreshPermissions() {
  try {
    const authState = useAuthStore.getState();
    if (!authState.userId) {
      console.warn("Cannot refresh permissions: No authenticated user");
      return null;
    }

    const permissions = await fetchUserPermissions(authState.userId);
    usePermissionStore.getState().setPermissions(permissions);
    return permissions;
  } catch (error) {
    console.error("Failed to refresh permissions:", error);
    throw error;
  }
}

/**
 * TanStack Query options for fetching user permissions.
 */
export const permissionQueryOptions = (userId: number | string, token: string) => ({
  queryKey: queryKeys.permissions.byUser(userId),
  queryFn: () => fetchPermissionsForUserById(userId),
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  retry: 1,
  refetchOnWindowFocus: false,
  enabled: !!userId && !!token,
});
